import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, AlertOctagon, AlertTriangle, Info, CheckCheck } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const SEV = {
  critical: { icon: AlertOctagon, cls: "bg-red-600 text-white border-red-700", dot: "bg-red-500", text: "text-red-700" },
  warning: { icon: AlertTriangle, cls: "bg-amber-100 text-amber-900 border-amber-300", dot: "bg-amber-500", text: "text-amber-700" },
  info: { icon: Info, cls: "bg-sky-100 text-sky-900 border-sky-300", dot: "bg-sky-500", text: "text-sky-700" },
};

export function useNotifications(intervalMs = 45000) {
  const { can, branch } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    if (!can("catalog.view") || document.hidden) return;
    try { setData((await api.get("/notifications?limit=40")).data); } catch { /* keep last */ }
  }, [can, branch]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    const t = setInterval(load, intervalMs);
    const onVis = () => !document.hidden && load();
    window.addEventListener("pf-notify-refresh", load);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); window.removeEventListener("pf-notify-refresh", load); document.removeEventListener("visibilitychange", onVis); };
  }, [load, intervalMs]);
  return { data, reload: load };
}

function Row({ n, onOpen }) {
  const S = SEV[n.severity] || SEV.info;
  return (
    <button type="button" onClick={() => onOpen(n)} data-testid={`notification-${n.id}`}
      className={`w-full text-left px-4 py-3 border-b last:border-0 flex gap-3 hover:bg-slate-50 transition-colors ${n.read ? "opacity-60" : ""}`}>
      <S.icon size={16} className={`mt-0.5 shrink-0 ${S.text}`} />
      <span className="min-w-0"><span className="block text-sm font-semibold leading-snug">{n.title}</span>
        {n.body && <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</span>}
        <span className="block text-[11px] text-slate-400 mt-1">{fmtDateTime(n.created_at)}</span></span>
      {!n.read && <span className={`ml-auto mt-1.5 h-2 w-2 rounded-full shrink-0 ${S.dot}`} />}
    </button>
  );
}

export function NotificationBell() {
  const nav = useNavigate();
  const { data, reload } = useNotifications();
  const [open, setOpen] = useState(false);
  const unread = data?.unread || 0;
  const markAll = async () => { await api.post("/notifications/read", { all: true }); reload(); };
  const openOne = async (n) => { setOpen(false); if (!n.read) api.post("/notifications/read", { ids: [n.id] }).then(reload); if (n.link) nav(n.link); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="relative h-10 w-10 grid place-items-center rounded-lg hover:bg-muted" data-testid="alerts-bell">
        <motion.span key={unread} animate={unread ? { rotate: [0, -14, 12, -8, 0] } : {}} transition={{ duration: 0.6 }}><Bell size={19} /></motion.span>
        {unread > 0 && <span className={`absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold grid place-items-center ${data.critical_unread ? "bg-red-600 animate-pulse" : "bg-amber-500"}`} data-testid="alerts-unread-count">{unread > 99 ? "99+" : unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b"><p className="font-semibold text-sm">Alerts {unread > 0 && <span className="text-muted-foreground font-normal">· {unread} unread</span>}</p>
          {unread > 0 && <button onClick={markAll} className="text-xs text-secondary inline-flex items-center gap-1 hover:underline" data-testid="notifications-mark-all"><CheckCheck size={13} />Mark all read</button>}</div>
        <div className="max-h-[420px] overflow-y-auto">
          <AnimatePresence initial={false}>
            {!data ? <p className="p-4 text-sm text-muted-foreground">Loading…</p> : !data.items.length ? <p className="p-4 text-sm text-muted-foreground" data-testid="notifications-empty">No alerts in the last 14 days.</p> :
              data.items.map((n) => <motion.div key={n.id} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}><Row n={n} onOpen={openOne} /></motion.div>)}
          </AnimatePresence>
        </div>
        <p className="px-4 py-2 border-t text-[11px] text-muted-foreground">Updates automatically. Urgent alerts are also emailed to the owner and branch managers.</p>
      </PopoverContent>
    </Popover>
  );
}

export function AlertTicker() {
  const nav = useNavigate();
  const { data } = useNotifications(30000);
  const [paused, setPaused] = useState(false);
  const items = (data?.items || []).filter((n) => !n.read).slice(0, 20);
  if (!items.length) return null;
  const row = [...items, ...items];
  return (
    <div className="mb-6 rounded-xl overflow-hidden border bg-gradient-to-r from-slate-900 via-[#0B192C] to-slate-900 text-white" data-testid="alert-ticker">
      <div className="flex items-center">
        <span className="shrink-0 px-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-red-600 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-white animate-ping" />Live alerts</span>
        <div className="flex-1 overflow-hidden" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="flex gap-3 w-max pf-marquee py-2 px-3" style={{ animationDuration: `${Math.max(20, items.length * 6)}s`, animationPlayState: paused ? "paused" : "running" }}>
            {row.map((n, i) => { const S = SEV[n.severity] || SEV.info; return (
              <button key={`${n.id}-${i}`} onClick={() => n.link && nav(n.link)} className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium whitespace-nowrap inline-flex items-center gap-1.5 ${S.cls}`} data-testid={i < items.length ? `ticker-item-${n.id}` : undefined}>
                <S.icon size={12} />{n.title}</button>); })}
          </div>
        </div>
      </div>
    </div>
  );
}
